import { useEffect, useRef, useState } from "react";

interface ShareLinkButtonProps {
  /** The root-relative share URL (path + lens query); the button prepends the origin on copy. */
  url: string;
}

/**
 * Copy a shareable link to the current Preset + lens, in the top bar beside the
 * Preset download. Gated by the parent to a shareable selection (a catalog Preset, both sides
 * for compare). Writes `origin + url` to the clipboard and flips to a transient
 * "Link copied" confirmation - a self-contained affordance, no cross-cutting toast plumbing.
 */
export function ShareLinkButton({ url }: ShareLinkButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // writeText resolves asynchronously, so the component can unmount before it does; `alive`
  // guards the confirmation so a late resolution neither updates an unmounted component nor
  // arms a reset timer that outlives cleanup
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
      clearTimeout(timer.current ?? undefined);
    },
    [],
  );

  const copy = () => {
    const origin = globalThis.location?.origin ?? "";
    // clipboard is absent in insecure contexts; degrade to no-op rather than throw
    navigator.clipboard
      ?.writeText(`${origin}${url}`)
      .then(() => {
        if (!alive.current) return;
        setCopied(true);
        clearTimeout(timer.current ?? undefined);
        timer.current = setTimeout(() => {
          if (alive.current) setCopied(false);
        }, 1600);
      })
      .catch(() => {
        // a denied clipboard (permission, blur) leaves the affordance unconfirmed; not fatal
      });
  };

  const label = copied ? "Link copied" : "Copy share link";
  return (
    <button
      type="button"
      className={copied ? "share-link copied" : "share-link"}
      onClick={copy}
      title={label}
      aria-label={label}
    >
      {copied ? (
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
      )}
    </button>
  );
}
